import type { MouseEvent } from 'react'
import { useVotes } from '../hooks/useVotes'
import type { VoteCounts, VoteValue } from '../hooks/useVotes'

// ── Full voting UI — EntryPage ────────────────────────────────

interface VoteButtonsProps {
  entryId: string
}

export function VoteButtons({ entryId }: VoteButtonsProps) {
  const { counts, myVote, loading, castVote } = useVotes(entryId)

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
      <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider">
        Is this translation correct?
      </h3>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => castVote('up')}
          disabled={loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors disabled:opacity-50
            ${myVote === 'up'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
              : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
        >
          <span className="text-base">👍</span>
          Looks correct
          {counts.up_count > 0 && (
            <span className={`font-bold ${myVote === 'up' ? 'text-emerald-600' : 'text-stone-400'}`}>
              {counts.up_count}
            </span>
          )}
        </button>

        <button
          onClick={() => castVote('down')}
          disabled={loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-colors disabled:opacity-50
            ${myVote === 'down'
              ? 'bg-rose-50 border-rose-300 text-rose-700'
              : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
        >
          <span className="text-base">👎</span>
          Something's wrong
          {counts.down_count > 0 && (
            <span className={`font-bold ${myVote === 'down' ? 'text-rose-600' : 'text-stone-400'}`}>
              {counts.down_count}
            </span>
          )}
        </button>
      </div>

      {counts.native_speaker_approved && (
        <div className="flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
          <span className="text-base mt-0.5">✓</span>
          <span>
            <strong>Native speaker verified</strong> — a Tamang speaker has confirmed this translation is correct.
          </span>
        </div>
      )}
      {counts.native_speaker_flagged && (
        <div className="flex items-start gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm">
          <span className="text-base mt-0.5">⚑</span>
          <span>
            <strong>Flagged by native speaker</strong> — a Tamang speaker has marked this as incorrect. Correction pending.
          </span>
        </div>
      )}

      {myVote && (
        <p className="text-xs text-stone-400">Click your selection again to undo your vote.</p>
      )}
    </div>
  )
}

// ── Compact Reddit-style buttons — EntryCard ─────────────────
// Receives pre-fetched data from the batch hook in SearchPage.
// stopPropagation prevents card navigation when voting.

interface CardVoteButtonsProps {
  entryId:  string
  counts:   VoteCounts | undefined
  myVote:   VoteValue
  castVote: (entryId: string, v: 'up' | 'down') => Promise<void>
}

export function CardVoteButtons({ entryId, counts, myVote, castVote }: CardVoteButtonsProps) {
  const up   = counts?.up_count   ?? 0
  const down = counts?.down_count ?? 0

  const handle = (e: MouseEvent, v: 'up' | 'down') => {
    e.stopPropagation()
    e.preventDefault()
    castVote(entryId, v)
  }

  return (
    <div
      className="flex items-center gap-1.5"
      onClick={e => e.stopPropagation()}
    >
      {/* Thumbs up */}
      <button
        onClick={e => handle(e, 'up')}
        className={`flex flex-col items-center px-2 py-0.5 rounded-lg border text-xs font-medium transition-colors min-w-[30px]
          ${myVote === 'up'
            ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
            : 'bg-white border-stone-300 text-stone-500 hover:border-emerald-300 hover:text-emerald-600'}`}
        aria-label="Vote correct"
      >
        <span>👍</span>
        <span>{up}</span>
      </button>

      {/* Thumbs down */}
      <button
        onClick={e => handle(e, 'down')}
        className={`flex flex-col items-center px-2 py-0.5 rounded-lg border text-xs font-medium transition-colors min-w-[30px]
          ${myVote === 'down'
            ? 'bg-rose-50 border-rose-400 text-rose-700'
            : 'bg-white border-stone-300 text-stone-500 hover:border-rose-300 hover:text-rose-600'}`}
        aria-label="Vote incorrect"
      >
        <span>👎</span>
        <span>{down}</span>
      </button>

      {/* Native speaker badges (compact) */}
      {counts?.native_speaker_approved && (
        <span className="text-emerald-600 text-xs font-bold" title="Native speaker verified">✓</span>
      )}
      {counts?.native_speaker_flagged && (
        <span className="text-rose-600 text-xs font-bold" title="Flagged by native speaker">⚑</span>
      )}
    </div>
  )
}
